import {
  DefaultButton,
  getTheme,
  Shimmer,
  Stack,
  Text,
  TextField
} from "@fluentui/react";
import { Fragment } from "react/jsx-runtime";

import {
  HAND_DELIVERY_TRACKING_CODE,
  getLineAdditionalServiceStatusLabel,
  isLineAdditionalServiceStepTerminal,
  lineAdditionalServiceTypeLabels,
  formatDeliveryAddress,
  paymentRequestStatusFormatters,
  simTypeOptions
} from "../../constants";
import {
  LineAdditionalService,
  LineAdditionalServiceInteractionAction,
  LineAdditionalServiceListItem,
  LineAdditionalServicePaymentRequest,
  LineAdditionalServiceStepName,
  LineAdditionalServiceType,
  LineAdditionalServiceUnit
} from "../../interfaces";

import { PaymentRequestTypeProviders } from "@/common/types/PaymentRequestTypes";
import Accordion from "@/components/Contracts/components/Accordion";
import { CopyPaste } from "@/components/CopyPaste";
import { currencyValueToLocaleString } from "@/utils/currencyValueToLocaleString";
import { formatDateAndTimeToString } from "@/utils/FormatDate";

interface LineAdditionalServiceCardProps {
  lineAdditionalService: LineAdditionalServiceListItem;
  hasPaymentLinkPollingTimedOut: boolean;
  isProcessing: boolean;
  onCancel: (service: LineAdditionalServiceListItem) => void;
  onCheckPaymentStatus: (service: LineAdditionalServiceListItem) => void;
  onProcessStep: (service: LineAdditionalServiceListItem) => void;
  onRefreshPaymentLinks: () => void;
}

function formatPaymentStatus(
  paymentRequest: LineAdditionalServicePaymentRequest
): string {
  return (
    paymentRequestStatusFormatters[paymentRequest.requestStatus]?.(
      paymentRequest
    ) ?? "Status do pagamento indisponível"
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <Stack styles={{ root: { minWidth: 70 } }}>
      <Text variant="small" styles={{ root: { color: "#605e5c" } }}>
        {label}
      </Text>
      <Text styles={{ root: { fontWeight: 600 } }}>{value}</Text>
    </Stack>
  );
}

function getStatusPillStyles(status: LineAdditionalServiceStepName) {
  if (status === LineAdditionalServiceStepName.PAID) {
    return {
      backgroundColor: "#fff4ce",
      color: "#815600"
    };
  }

  if (status === LineAdditionalServiceStepName.CANCELED) {
    return {
      backgroundColor: "#fde7e9",
      color: "#a80000"
    };
  }

  if (status === LineAdditionalServiceStepName.CREATED) {
    return {
      backgroundColor: "#eff6fc",
      color: "#395ca3"
    };
  }

  return {
    backgroundColor: "#dff6dd",
    color: "#0b6a0b"
  };
}

function ServiceRequestDetails({
  service
}: {
  service: LineAdditionalService;
}): JSX.Element {
  const simTypeLabel = simTypeOptions.find(
    option => option.key === service.type
  )?.text;

  switch (service.type) {
    case LineAdditionalServiceType.DATA_BONUS:
      return (
        <Stack tokens={{ childrenGap: 4 }}>
          {service.notes?.details && (
            <InfoItem label="Motivo" value={service.notes.details} />
          )}
        </Stack>
      );

    case LineAdditionalServiceType.PHYSICAL_CHIP: {
      const { notes } = service;

      return (
        <Stack tokens={{ childrenGap: 4 }}>
          {simTypeLabel && (
            <InfoItem label="Tipo de SIM" value={simTypeLabel} />
          )}
          {notes?.details && <InfoItem label="Motivo" value={notes.details} />}
          {notes?.deliveryAddress && (
            <>
              <InfoItem
                label="Endereço de entrega"
                value={formatDeliveryAddress(notes.deliveryAddress)}
              />
              {(notes.iccid || notes.trackingCode || notes.rescueProtocol) && (
                <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
                  {notes.iccid && (
                    <InfoItem label="ICCID" value={notes.iccid} />
                  )}
                  {notes.trackingCode && (
                    <InfoItem
                      label={
                        notes.trackingCode === HAND_DELIVERY_TRACKING_CODE
                          ? "Forma de entrega"
                          : "Código de rastreamento"
                      }
                      value={notes.trackingCode}
                    />
                  )}
                  {notes.rescueProtocol && (
                    <InfoItem
                      label="Protocolo de resgate"
                      value={notes.rescueProtocol}
                    />
                  )}
                </Stack>
              )}
            </>
          )}
        </Stack>
      );
    }

    case LineAdditionalServiceType.ESIM: {
      const { notes } = service;

      return (
        <Stack tokens={{ childrenGap: 4 }}>
          {simTypeLabel && (
            <InfoItem label="Tipo de SIM" value={simTypeLabel} />
          )}
          {notes?.details && <InfoItem label="Motivo" value={notes.details} />}
          {notes?.eSimInfo && (
            <Stack horizontal wrap tokens={{ childrenGap: 16 }}>
              <InfoItem label="IMEI" value={notes.eSimInfo.IMEI} />
              <InfoItem label="EID" value={notes.eSimInfo.EID} />
              <InfoItem label="Aparelho" value={notes.eSimInfo.deviceModel} />
            </Stack>
          )}
        </Stack>
      );
    }
  }
}

export function LineAdditionalServicesCard({
  lineAdditionalService,
  hasPaymentLinkPollingTimedOut,
  isProcessing,
  onCancel,
  onCheckPaymentStatus,
  onProcessStep,
  onRefreshPaymentLinks
}: LineAdditionalServiceCardProps): JSX.Element {
  const theme = getTheme();
  const isTerminal = isLineAdditionalServiceStepTerminal(lineAdditionalService);
  const nextInteractionAction = isTerminal
    ? undefined
    : lineAdditionalService.nextStep?.interactionAction;
  const isPendingPayment =
    nextInteractionAction ===
    LineAdditionalServiceInteractionAction.CONFIRM_PAYMENT;

  const processStepButtonText =
    lineAdditionalService.nextStep?.interactionName ??
    lineAdditionalService.nextStep?.friendlyName;

  const canConfirmPayment =
    isPendingPayment &&
    lineAdditionalService.paymentRequest?.typeProvider ===
      PaymentRequestTypeProviders.INVOICE_DEBIT;

  const canCheckPaymentStatus =
    isPendingPayment &&
    lineAdditionalService.paymentRequest?.typeProvider ===
      PaymentRequestTypeProviders.PIX &&
    Boolean(lineAdditionalService.paymentRequest?.transactionId);

  const canConfirmFulfillment =
    nextInteractionAction ===
    LineAdditionalServiceInteractionAction.DELIVER_SERVICE;

  const canProcessStep = canConfirmPayment || canConfirmFulfillment;
  const canCancel = !isTerminal && Boolean(lineAdditionalService.nextStep);

  const statusPillStyles = getStatusPillStyles(
    (isTerminal
      ? lineAdditionalService.currentStep
      : (lineAdditionalService.nextStep ?? lineAdditionalService.currentStep)
    ).internalName
  );
  const quantity = `${lineAdditionalService.quantity} ${
    lineAdditionalService.unit === LineAdditionalServiceUnit.GB
      ? "GB"
      : "unidade"
  }`;
  const paymentLink = lineAdditionalService.paymentRequest?.transactionId
    ? `${import.meta.env.VITE_MEMBER_PORTAL_URL}/payments/${
        lineAdditionalService.paymentRequest.transactionId
      }`
    : "";

  const isPixPayment =
    lineAdditionalService.paymentRequest?.typeProvider ===
    PaymentRequestTypeProviders.PIX;

  const isWaitingForPaymentLink =
    isPendingPayment && isPixPayment && !paymentLink;

  const displayActionButtons =
    canCancel ||
    canCheckPaymentStatus ||
    (canProcessStep && processStepButtonText);

  return (
    <Stack
      tokens={{ childrenGap: 12 }}
      as="section"
      style={{
        border: `1px solid ${theme.palette.neutralLight}`,
        borderRadius: 6,
        boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
        padding: 16
      }}
    >
      <Stack horizontal verticalAlign="center" wrap tokens={{ childrenGap: 8 }}>
        <Text variant="mediumPlus" styles={{ root: { fontWeight: 700 } }}>
          {lineAdditionalService.id} -{" "}
          {lineAdditionalServiceTypeLabels[lineAdditionalService.type]}
        </Text>
        <Text
          variant="small"
          styles={{
            root: {
              alignSelf: "flex-start",
              backgroundColor: statusPillStyles.backgroundColor,
              borderRadius: 4,
              color: statusPillStyles.color,
              fontSize: 12,
              fontWeight: 600,
              padding: "3px 10px",
              whiteSpace: "nowrap"
            }
          }}
        >
          {getLineAdditionalServiceStatusLabel(lineAdditionalService)}
        </Text>
      </Stack>

      <Stack
        horizontal
        wrap
        tokens={{ childrenGap: 16 }}
        styles={{
          root: {
            borderTop: `1px solid ${theme.palette.neutralLighter}`,
            paddingTop: 12
          }
        }}
      >
        {lineAdditionalService.unit === LineAdditionalServiceUnit.GB && (
          <InfoItem label="Quantidade" value={quantity} />
        )}
        <InfoItem
          label="Valor"
          value={currencyValueToLocaleString(lineAdditionalService.value / 100)}
        />
        <InfoItem
          label="Criado em"
          value={formatDateAndTimeToString(lineAdditionalService.createdAt)}
        />
        <InfoItem
          label="Criado por"
          value={lineAdditionalService.requestedByUser.fullName}
        />
        {lineAdditionalService.paymentRequest && (
          <InfoItem
            label="Pagamento"
            value={formatPaymentStatus(lineAdditionalService.paymentRequest)}
          />
        )}
      </Stack>

      {lineAdditionalService.notes && (
        <Stack
          tokens={{ childrenGap: 4 }}
          styles={{
            root: {
              borderTop: `1px solid ${theme.palette.neutralLighter}`,
              paddingTop: 8
            }
          }}
        >
          <Accordion
            title="Dados da solicitação"
            content={<ServiceRequestDetails service={lineAdditionalService} />}
            compactMode={false}
            showValue={false}
            isCloseDefault
            contentBackgroundColor="transparent"
          />
        </Stack>
      )}

      {isWaitingForPaymentLink && (
        <Stack
          tokens={{ childrenGap: 8 }}
          styles={{
            root: {
              backgroundColor: theme.palette.neutralLighterAlt,
              padding: 10
            }
          }}
        >
          {hasPaymentLinkPollingTimedOut ? (
            <Stack
              horizontal
              wrap
              verticalAlign="center"
              horizontalAlign="space-between"
              tokens={{ childrenGap: 8 }}
            >
              <Text variant="small">
                A cobrança ainda está sendo processada.
              </Text>
              <DefaultButton
                iconProps={{ iconName: "Refresh" }}
                text="Atualizar"
                disabled={isProcessing}
                onClick={onRefreshPaymentLinks}
              />
            </Stack>
          ) : (
            <Fragment>
              <Text variant="small">Gerando link de cobrança</Text>
              <Shimmer />
            </Fragment>
          )}
        </Stack>
      )}

      {isPendingPayment && paymentLink && (
        <Stack
          tokens={{ childrenGap: 8 }}
          styles={{
            root: {
              backgroundColor: theme.palette.neutralLighterAlt,
              padding: 10
            }
          }}
        >
          <Stack
            horizontal
            wrap
            verticalAlign="center"
            tokens={{ childrenGap: 8 }}
          >
            <CopyPaste
              valueToCopy={paymentLink}
              tooltipText="Copiar link de cobrança"
            >
              <Text variant="small">Envie o link de Cobrança ao Cooperado</Text>
            </CopyPaste>
          </Stack>

          <Stack.Item grow styles={{ root: { minWidth: 0, width: "100%" } }}>
            <TextField value={paymentLink} disabled />
          </Stack.Item>
        </Stack>
      )}

      {displayActionButtons && (
        <Stack
          horizontal
          wrap
          horizontalAlign="end"
          tokens={{ childrenGap: 8 }}
          styles={{
            root: {
              paddingTop: 12
            }
          }}
        >
          {canCancel && (
            <DefaultButton
              text="Cancelar serviço adicional"
              disabled={isProcessing}
              onClick={() => onCancel(lineAdditionalService)}
              styles={{ root: { color: theme.palette.redDark } }}
            />
          )}
          {canCheckPaymentStatus && (
            <DefaultButton
              text="Verificar status da Cobrança"
              disabled={isProcessing}
              onClick={() => onCheckPaymentStatus(lineAdditionalService)}
            />
          )}
          {canProcessStep && processStepButtonText && (
            <DefaultButton
              text={processStepButtonText}
              disabled={isProcessing}
              onClick={() => onProcessStep(lineAdditionalService)}
            />
          )}
        </Stack>
      )}
    </Stack>
  );
}
